import React, { useState } from 'react';
import { Form, Input, Button, Select, Modal, DatePicker, Radio, message } from 'antd';
import { BiPlusCircle } from "react-icons/bi";
import locale from 'antd/es/date-picker/locale/pt_BR';

import api from '../../../api';
import formatPriceForInput from '../../../helpers/formatPrice';
import './add-interested-customer.css';

const weekDays = [
    { value: 'Segunda-feira', label: 'Segunda' },
    { value: 'Terça-feira', label: 'Terça' },
    { value: 'Quarta-feira', label: 'Quarta' },
    { value: 'Quinta-feira', label: 'Quinta' },
    { value: 'Sexta-feira', label: 'Sexta' },
    { value: 'Sábado', label: 'Sábado' },
    { value: 'Domingo', label: 'Domingo' }
];

// Mapeamento do índice do dia da semana (0=Domingo) para o valor
const weekDayMap = {
    0: 'Domingo',
    1: 'Segunda-feira',
    2: 'Terça-feira',
    3: 'Quarta-feira',
    4: 'Quinta-feira',
    5: 'Sexta-feira',
    6: 'Sábado'
};

function AddInterestedCustomer({ users, loadData, userData, isAdmin }) {
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [price, setPrice] = useState('');

    // Verifica se pode selecionar vendedor (admin ou supervisor com subordinados)
    const canSelectSeller = users.length > 0;

    function changePrice(value) {
        setPrice(formatPriceForInput(value));
    }

    function handleDateChange(date) {
        if (date) {
            const dayOfWeek = date.day();
            form.setFieldsValue({ weekDay: weekDayMap[dayOfWeek] });
        }
    }

    function openModal() {
        form.resetFields();
        setPrice('');
        // Se não pode selecionar seller, define o próprio usuário
        if (!canSelectSeller && userData?._id) {
            form.setFieldsValue({ seller: userData.id });
        }
        setModalVisible(true);
    }

    function closeModal() {
        setModalVisible(false);
        form.resetFields();
        setPrice('');
    }

    async function onFinish(values) {
        setLoading(true);

        try {
            const data = {
                seller: canSelectSeller ? values.seller : userData.id,
                company: values.company,
                weekDay: values.weekDay,
                interestDate: values.interestDate.toISOString(),
                priority: values.priority || 'medium',
                hasProposal: values.hasProposal || false,
                originListing: values.originListing,
                sellTo: values.sellTo,
                value: price || '',
                observation: values.observation,
                userId: userData.id
            };

            await api.post('/interested-customers', data);
            
            message.success('Cliente interessado cadastrado com sucesso!');
            closeModal();
            loadData();
        } catch (error) {
            console.error('Erro ao cadastrar:', error);
            message.error('Erro ao cadastrar cliente interessado');
        }

        setLoading(false);
    }

    return (
        <>
            <div className="add-interested-customer" onClick={openModal}>
                <BiPlusCircle size="20px" />
                <span>Adicionar Cliente Interessado</span>
            </div>

            <Modal
                title="Novo Cliente Interessado"
                visible={modalVisible}
                onCancel={closeModal}
                footer={null}
                width={700}
                centered
                maskClosable={false}
            >
                <div className="add-interested-customer-form">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        initialValues={{
                            priority: 'medium',
                            hasProposal: false,
                            seller: !canSelectSeller ? userData?._id : undefined
                        }}
                    >
                        <div className="form-row">
                            {canSelectSeller &&
                                <Form.Item
                                    name="seller"
                                    label="Vendedor"
                                    rules={[{ required: true, message: 'Vendedor é obrigatório' }]}
                                >
                                    <Select
                                        placeholder="Selecione o vendedor"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {users.map(user => (
                                            <Select.Option key={user._id} value={user._id}>
                                                {user.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            }

                            <Form.Item
                                name="company"
                                label="Empresa"
                                rules={[{ required: true, message: 'Empresa é obrigatória' }]}
                            >
                                <Input placeholder="Nome da empresa" />
                            </Form.Item>
                        </div>

                        <div className="form-row"> 
                            <Form.Item
                                name="interestDate"
                                label="Data do Interesse"
                                rules={[{ required: true, message: 'Data é obrigatória' }]}
                            >
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    placeholder="Selecione a data"
                                    locale={locale}
                                    style={{ width: '100%' }}
                                    onChange={handleDateChange}
                                />
                            </Form.Item>

                            <Form.Item
                                name="weekDay"
                                label="Dia da Semana"
                            >
                                <Select placeholder="Selecione">
                                    {weekDays.map(day => (
                                        <Select.Option key={day.value} value={day.value}>
                                            {day.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </div>

                        <div className="form-row">
                            <Form.Item
                                name="priority"
                                label="Prioridade"
                            >
                                <Radio.Group className="priority-selector">
                                    <Radio.Button value="high" className="high">Alta</Radio.Button>
                                    <Radio.Button value="medium" className="medium">Média</Radio.Button>
                                    <Radio.Button value="low" className="low">Baixa</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                name="hasProposal"
                                label="Proposta Enviada"
                            >
                                <Radio.Group className="proposal-selector">
                                    <Radio.Button value={true}>Sim</Radio.Button>
                                    <Radio.Button value={false}>Não</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </div>

                        <div className="form-row">
                            <Form.Item
                                name="originListing"
                                label="Origem da Listagem"
                            >
                                <Input placeholder="Ex: Amil, SulAmérica, Notredame..." />
                            </Form.Item>

                            <Form.Item
                                name="sellTo"
                                label="Vender"
                            >
                                <Input placeholder="Ex: Porto Seguro, Bradesco..." />
                            </Form.Item>
                        </div>

                        <Form.Item label="Valor">
                            <Input
                                value={price}
                                onChange={(e) => changePrice(e.target.value)}
                                placeholder="R$ 0,00"
                                style={{ width: '50%' }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="observation"
                            label="Observação / Status"
                        >
                            <Input.TextArea
                                rows={3}
                                placeholder="Digite uma observação inicial..."
                            />
                        </Form.Item>

                        <div className="add-interested-customer-form-footer">
                            <Button onClick={closeModal}>
                                Cancelar
                            </Button>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                Cadastrar
                            </Button>
                        </div>
                    </Form>
                </div>
            </Modal>
        </>
    );
}

export default AddInterestedCustomer;
